import dagre from 'dagre';

import type { AgentEdge, AgentGraph, AgentNode } from '@repo/shared-types';
import type { Edge, Node } from '@xyflow/react';

export const AGENT_NODE_COLORS = {
  ai_agent: {
    tint: 'bg-indigo-100 dark:bg-indigo-500/15',
    icon: 'text-indigo-600 dark:text-indigo-400',
    handle: '!bg-indigo-500',
  },
  human: {
    tint: 'bg-amber-100 dark:bg-amber-500/15',
    icon: 'text-amber-600 dark:text-amber-400',
    handle: '!bg-amber-500',
  },
} as const;

export const createDefaultAiAgent = (position: { x: number; y: number }): AgentNode => ({
  id: crypto.randomUUID(),
  name: '',
  role: '',
  type: 'ai_agent',
  is_entry: false,
  tools: [],
  position,
});

export const createDefaultHumanAgent = (position: { x: number; y: number }): AgentNode => ({
  id: crypto.randomUUID(),
  name: '',
  role: '',
  type: 'human',
  is_entry: false,
  tools: [],
  transfer_target_key: '',
  position,
});

/** Maximum number of tool prerequisites allowed on a single edge. */
export const MAX_REQUIRES_TOOLS = 10;

/** Parse a comma-separated tool list into a deduplicated string array (capped at {@link MAX_REQUIRES_TOOLS}). */
export const parseRequiresTools = (input: string): string[] => {
  const seen = new Set<string>();
  return input
    .split(',')
    .map((s) => s.trim())
    .filter((s) => {
      if (!s || seen.has(s)) return false;
      seen.add(s);
      return true;
    })
    .slice(0, MAX_REQUIRES_TOOLS);
};

export const agentGraphToFlow = (graph: AgentGraph): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = graph.agents.map((agent) => ({
    id: agent.id,
    type: agent.type === 'human' ? 'humanAgent' : 'aiAgent',
    position: agent.position,
    data: { ...agent },
  }));

  const agentsByName = new Map(graph.agents.map((a) => [a.name, a]));

  const edges: Edge[] = graph.edges.reduce<Edge[]>((acc, edge) => {
    const sourceAgent = agentsByName.get(edge.source);
    const targetAgent = agentsByName.get(edge.target);
    if (!sourceAgent || !targetAgent) return acc;
    acc.push({
      id: edge.id,
      source: sourceAgent.id,
      target: targetAgent.id,
      data: {
        description: edge.description,
        ...(edge.requires_tools?.length ? { requires_tools: edge.requires_tools } : {}),
      },
    });
    return acc;
  }, []);

  return { nodes, edges };
};

export const flowToAgentGraph = (
  nodes: Node[],
  edges: Edge[],
  policy?: AgentGraph['conversation_flow'],
): AgentGraph => {
  const agents: AgentNode[] = nodes.map((node) => ({
    ...(node.data as AgentNode),
    position: node.position,
  }));

  const agentsById = new Map(agents.map((a) => [a.id, a]));

  const graphEdges: AgentEdge[] = edges.reduce<AgentEdge[]>((acc, edge) => {
    const sourceAgent = agentsById.get(edge.source);
    const targetAgent = agentsById.get(edge.target);
    if (!sourceAgent || !targetAgent) return acc;
    // Drop edges where either agent has no name (work-in-progress nodes)
    if (!sourceAgent.name || !targetAgent.name) return acc;
    const edgeData = edge.data as { description?: string; requires_tools?: string[] } | undefined;
    const requiresTools = edgeData?.requires_tools;
    acc.push({
      id: edge.id,
      source: sourceAgent.name,
      target: targetAgent.name,
      description: edgeData?.description ?? '',
      ...(requiresTools?.length ? { requires_tools: requiresTools } : {}),
    });
    return acc;
  }, []);

  return { agents, edges: graphEdges, ...(policy ? { conversation_flow: policy } : {}) };
};

export const autoLayoutAgentGraph = (nodes: Node[], edges: Edge[]): Node[] => {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 100, ranksep: 120 });

  nodes.forEach((node) => {
    g.setNode(node.id, { width: 200, height: 80 });
  });

  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  return nodes.map((node) => {
    const pos = g.node(node.id);
    return { ...node, position: { x: pos.x - 100, y: pos.y - 40 } };
  });
};
